import * as React from 'react';
import styled, { css } from 'styled-components';
import { IToggleInputProps } from './toggle-input-types';
import { excludeKeys } from '../../utils';
import { FlexBox } from '../flex-box';
import { selectThemeSemantics } from '../../styling/theming';

interface IToggleInputState {
    checked: boolean;
    isHovered?: boolean;
    isFocused?: boolean;
}

const leftLabelStyle = css`
    margin-right:  ${selectThemeSemantics(({ gap }) => gap.xl)};
`;

const rightLabelStyle = css`
    margin-left:  ${selectThemeSemantics(({ gap }) => gap.xl)};
`;

const Label = styled.span.attrs(() => ({
    className: String('ap-toggleInput-label')
})) <Pick<IToggleInputProps, 'required' | 'labelPosition'>>`
    ${({ labelPosition }) => (labelPosition === 'left' ? leftLabelStyle : rightLabelStyle)};
    &:after {
        content: ${({ required }) => (required ? '\'*\'' : '')};
        color: ${selectThemeSemantics(({ palette }) => palette.red)};
    }
`;

const NativeCheckbox = styled.input.attrs(() => ({
    type: 'checkbox'
}))`
    position: absolute;
    top: 0;
    opacity: 0;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const mediumSizeToggleStyle = css`
    width: 44px;
    height: 24px;
`;

const smallSizeToggleStyle = css`
    width: 30px;
    height: 18px;
`;

const mediumSizeKnobStyle = css`
    width: 20px;
    height: 20px;
`;

const smallSizeKnobStyle = css`
    width: 14px;
    height: 14px;
`;

const hoveredToggleStyle = css<Pick<IToggleInputProps, 'checked'>>`
    background-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.hoveredChecked.backgroundColor : toggleInput.colors.hovered.backgroundColor)};
`;

const defaultToggleStyle = css<Pick<IToggleInputProps, 'checked'>>`
    background-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.checked.backgroundColor : toggleInput.colors.default.backgroundColor)};
`;

const disabledToggleStyle = css<Pick<IToggleInputProps, 'checked'>>`
    background-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.disabledChecked.backgroundColor : toggleInput.colors.disabled.backgroundColor)};
`;

const hoveredKnobStyle = css<Pick<IToggleInputProps, 'checked'>>`
    background-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.hoveredChecked.knobBackgroundColor : toggleInput.colors.hovered.knobBackgroundColor)};
    border-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.hoveredChecked.knobBorderColor : toggleInput.colors.hovered.knobBorderColor)};
`;

const disabledKnobStyle = css<Pick<IToggleInputProps, 'checked'>>`
    background-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.disabledChecked.knobBackgroundColor : toggleInput.colors.disabled.knobBackgroundColor)};
    border-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.disabledChecked.knobBorderColor : toggleInput.colors.disabled.knobBorderColor)};
`;

const defaultKnobStyle = css<Pick<IToggleInputProps, 'checked'>>`
    background-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.checked.knobBackgroundColor : toggleInput.colors.default.knobBackgroundColor)};
    border-color: ${({ theme: { toggleInput }, checked }) => (checked ? toggleInput.colors.checked.knobBorderColor : toggleInput.colors.default.knobBorderColor)};
`;

const focusedKnobStyle = css<Pick<IToggleInputProps, 'checked'>>`
    box-shadow: 0px 0px 4px ${({ theme: { semantics: { palette } } }) => palette.themePrimary};
`;

const ToggleField = styled(FlexBox).attrs(() => ({
    className: String('ap-toggle1')
})) <Pick<IToggleInputProps, 'checked' | 'disabled' | 'size'> & Pick<IToggleInputState, 'isHovered' | 'isFocused'>>`
    ${({ size }) => (size === 'small' ? smallSizeToggleStyle : mediumSizeToggleStyle)};
    border-radius: ${({ theme: { toggleInput } }) => toggleInput.composites.borderRadius};
    flex-direction:${({ checked }) => (checked ? 'row-reverse' : 'row')};
    ${({ isHovered, disabled }) => (
        disabled ?
            disabledToggleStyle
            : (isHovered ? hoveredToggleStyle : defaultToggleStyle)
    )};

    ${({ isFocused, disabled }) => (
        disabled ?
            disabledToggleStyle
            : (isFocused ? focusedKnobStyle : defaultToggleStyle)
    )};
    `;

const Knob = styled.span.attrs(() => ({
    className: String('ap-toggleKnob')
})) <Pick<IToggleInputProps, 'checked' | 'disabled' | 'size'> & Pick<IToggleInputState, 'isHovered'>>`
    margin: ${selectThemeSemantics(({ gap }) => gap.s)};
    border-radius: ${({ theme: { toggleInput } }) => toggleInput.composites.knobBorderRadius};
    border-width: ${({ theme: { toggleInput } }) => toggleInput.composites.knobBorderWidth};
    box-sizing: border-box;
    box-shadow: ${({ theme: { toggleInput } }) => toggleInput.composites.boxShadow};
    ${({ size }) => (size === 'small' ? smallSizeKnobStyle : mediumSizeKnobStyle)};

    ${({ isHovered, disabled }) => (
        disabled ?
            disabledKnobStyle
            : (isHovered ? hoveredKnobStyle : defaultKnobStyle)
    )};
`;


class ToggleInputBase extends React.PureComponent<IToggleInputProps, IToggleInputState> {
    static getDerivedStateFromProps(props: IToggleInputProps) {
        if (props.checked === undefined) {
            return null;
        }
        return { checked: props.checked };
    }

    constructor(props: IToggleInputProps) {
        super(props);
        const { defaultChecked = false } = props;
        this.state = {
            checked: defaultChecked
        };
    }

    render() {
        const { disabled, className, hidden, size, title } = this.props;
        const { checked, isHovered, isFocused } = this.state;
        const toggleInputProps = excludeKeys(this.props, 'label', 'onValueChange', 'className', 'size');

        return hidden ? null : (
            <label className={className} onMouseEnter={this.setHoveredState} onMouseLeave={this.resetHoveredState}
                onFocus={this.onFocus} onBlur={this.onBlur} title={title}>
                {this.renderLabel()}
                <ToggleField checked={checked} disabled={disabled} size={size} isHovered={isHovered} isFocused={isFocused}>
                    <Knob checked={checked} disabled={disabled} size={size} isHovered={isHovered} />
                </ToggleField>
                <NativeCheckbox {...toggleInputProps} checked={checked} onChange={this.onChange} />
            </label>
        );
    }

    private setHoveredState = () => {
        this.setState({ isHovered: true });
    }

    private resetHoveredState = () => {
        this.setState({ isHovered: false });
    }

    private onFocus = () => {
        this.setState({ isFocused: true });
    }

    private onBlur = () => {
        this.setState({ isFocused: false });
    }

    private renderLabel() {
        const { required, label, labelPosition } = this.props;
        return label ? <Label required={required} labelPosition={labelPosition}>{label}</Label> : null;
    }

    private onChange = (e: React.FormEvent<HTMLInputElement>) => {
        const { checked, onValueChange, onChange } = this.props;
        const newValue = (e.target as HTMLInputElement).checked;
        if (onChange !== undefined) {
            onChange(e);
        }
        if (onValueChange !== undefined) {
            onValueChange(newValue);
        }
        if (checked === undefined) {
            this.setState({ checked: newValue });
        }
    }
}

export const ToggleInput = styled(ToggleInputBase).attrs(() => ({
    className: String('ap-toggleInput-wrapper')
}))`
    display: inline-flex;
    position: relative;
    align-items: center;
    flex-direction: ${({ labelPosition }) => (labelPosition === 'left' ? 'row' : 'row-reverse')};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;
